'use client';

/**
 * usePlanLenses — the ONE fetch behind the Plan Lenses cards. Resolves the
 * strategy through the capability store, never by literal name; React Query
 * dedupes the card mounts into a single BFF call.
 *
 * The cards are RENDERERS. Every position, percentage, count, sign, grouping,
 * ordering and span arrives already computed — no arithmetic, filtering,
 * grouping, sign handling or fallback value lives in a card. If a card needs
 * it, the BFF ships it. The types below mirror the payload exactly.
 *
 * @see klyde_edge/klyde_intelligence/projects/plan_lenses.py
 *
 * @component_role: Hook
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';

export interface TierCount { tier: string; count: number }
export interface PriceRange { low: number; high: number }
export interface CandidateOption {
  name: string | null;
  vendor: string | null;
  unit_price: number | null;
  tier: string;
}

export interface PlanLensesGap {
  pattern: string;
  data: {
    budget: {
      approved_budget: number | null;
      plan_total_selected: number;
      plan_total_at_lowest_tier: number | null;
      plan_total_at_mid_tier: number | null;
      plan_total_at_premium_tier: number | null;
      overspent_by: number | null;
      lowest_tier_vs_budget: 'over' | 'under' | 'at' | null;
      lowest_tier_gap: number | null;
      budget_left_after_selected: number | null;
      committed_po_value: number | null;
      total_rooms: number | null;
      budget_per_room: number | null;
    };
    time: { today: string; target_date: string | null; days_left: number | null };
    quality: { selected_by_tier: TierCount[] };
    requirements: { total: number; selected: number; open: number };
    tier_ladder: { tier: string; total: number }[];
    paint: {
      bar_selected_pct: number | null;
      bar_budget_pct: number | null;
      bar_lowest_tier_pct: number | null;
      bar_left_pct: number | null;
      bar_over_pct: number | null;
    };
  };
}

export interface VendorPlacement {
  vendor: string;
  share_pct: number;
  selected_total: number;
  product_count: number;
  candidate_on_open_requirements: number;
  open_requirements_rfq_out: number;
  open_requirements_quoted: number;
  has_selected_spend: boolean;
  cell: string;
  paint: {
    lane: 'with_spend' | 'no_spend';
    x_pct: number | null;
    lane_x_pct: number | null;
    y_pct: number;
    label_align: 'start' | 'center' | 'end';
    show_label: boolean;
  };
}

export interface PlanLensesVendors {
  pattern: string;
  data: {
    open_requirement_count: number;
    reach_boundary_requirements: number | null;
    cells: { cell: string; vendors: VendorPlacement[] }[];
    paint: {
      reach_boundary_y_pct: number | null;
      x_ticks: { value: number; pct: number }[];
      y_ticks: { value: number; pct: number }[];
    };
  };
}

export interface PlanLensesDecisions {
  pattern: string;
  data: {
    selected_requirements: {
      requirement: string | null;
      rooms: string[];
      quantity: number | null;
      pick: CandidateOption;
      pick_line_total: number | null;
      alternatives: CandidateOption[];
    }[];
  };
}

export interface PlanLensesConcentration {
  pattern: string;
  data: {
    vital_few_boundary_pct: number;
    vital_few_count: number;
    vital_few_reached_share_pct: number | null;
    product_count: number;
    products: {
      product_name: string | null;
      vendors: string[];
      quantity: number | null;
      selected_total: number;
      share_pct: number;
      cumulative_share_pct: number;
      in_vital_few: boolean;
      ends_vital_few: boolean;
      paint: { bar_pct: number | null };
    }[];
  };
}

export interface PlanLensesKnowledge {
  pattern: string;
  data: {
    requirement_count: number;
    selected_count: number;
    open_count: number;
    selected_quoted_count: number;
    selected_unquoted_count: number;
    selected_fully_allocated_count: number;
    open_with_rfq_out_count: number;
    open_with_quote_count: number;
    open_without_quote_count: number;
    committed_po_value: number | null;
    selected: {
      requirement: string | null;
      requirement_id: string;
      rooms: string[];
      quoted: boolean;
      fully_allocated: boolean;
      reached_sent_rfq: boolean;
    }[];
    open: {
      requirement: string | null;
      requirement_id: string;
      rooms: string[];
      quantity: number | null;
      candidate_count: number;
      candidate_tiers: TierCount[];
      unit_price_range: PriceRange | null;
      quoted_candidates: number;
      candidates_with_sent_rfq: number;
    }[];
    paint: { open_pct: number | null; selected_unquoted_pct: number | null; selected_quoted_pct: number | null };
  };
}

export interface KpiChip { value: number | null; text: string | null; format: 'money' | 'integer' | 'percent'; label: string | null }
export interface Kpi { value: number | null; format: 'money' | 'integer' | 'percent'; label: string; chip: KpiChip | null }

export interface RfqRow {
  vendor: string;
  status: string;
  products_requested: number;
  products_quoted: number;
  candidate_lines_covered: number;
  deadline: string | null;
  days_to_deadline: number | null;
}

/** Where the plan is in the buying cycle — counts in named units (see plan_lenses.ProcurementData). */
export interface PlanLensesProcurement {
  pattern: string;
  data: {
    rfq_count: number;
    rfqs_by_status: { status: string; count: number }[];
    vendors_asked: number;
    vendors_responded: number;
    earliest_deadline: string | null;
    days_to_earliest_deadline: number | null;
    candidate_lines: number;
    candidate_lines_reached_by_sent_rfq: number;
    candidate_lines_quoted: number;
    products_requested: number;
    products_quoted: number;
    purchase_order_value: number | null;
    rfqs: RfqRow[];
  };
}

export interface PlanLensesData {
  gap: PlanLensesGap;
  vendors: PlanLensesVendors;
  decisions: PlanLensesDecisions;
  concentration: PlanLensesConcentration;
  knowledge: PlanLensesKnowledge;
  procurement?: PlanLensesProcurement | null;
  kpis: Record<string, Kpi>;
  labels: Record<string, string>;
}

export function usePlanLenses() {
  const { mcpId } = useViewportSelection();
  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'plan_lenses'));
  const { data, isLoading } = useStrategyData<PlanLensesData>(
    strategy, { semantic_id: mcpId ?? '' }, !!mcpId && !!strategy,
  );
  const queryKey = strategy ? ['strategy-data', strategy, { semantic_id: mcpId ?? '' }] : null;
  return { mcpId, strategy, d: data?.data, isLoading, queryKey };
}

// Whole dollars arrive from the BFF; this only formats them for display.
export const money = (v: number) => `$${v.toLocaleString()}`;

// One colour per benchmark tier, shared by every Plan Lenses card
// (presentation only; `lowest` is the budget tier under its plan-total name).
export const TIER_COLOR: Record<string, string> = {
  budget: '#e0a94f', lowest: '#e0a94f', mid: '#7fa2ff', premium: '#c3a3ff', unclassified: '#8d8f9b',
};
